import { ConnectedWallet } from '@privy-io/react-auth';
import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';

import {
  COOL_ADDRESS_ARC,
  MAIN_USD_STABLE_ADDRESSES,
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID,
  USDC_ADDRESSES,
} from '@/constants/addresses';
import { CHAIN_IDS, SUPPORTED_CHAIN_IDS } from '@/constants/chain';

import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';

import type { Price, Quote } from './0x.types';
import { ChainAddress } from './models';
import { isTokenAddressNativeAsset, isTokenAddressWrappedNativeAsset } from './multichain';
import { getChainIdNumberFromCaip2ChainId } from './privy';

export function isWrap(
  sellTokenAddress: string,
  buyTokenAddress: string,
  chainId: number,
): boolean {
  return (
    !!(
      isTokenAddressNativeAsset(sellTokenAddress, chainId) &&
      isTokenAddressWrappedNativeAsset(buyTokenAddress, chainId)
    ) ||
    !!(
      isTokenAddressWrappedNativeAsset(sellTokenAddress, chainId) &&
      isTokenAddressNativeAsset(buyTokenAddress, chainId)
    )
  );
}

/**
 * The static default token pair for a given chain: sell biases to USDC, buy
 * biases to the chain's native token except Arc, which defaults to COOL. Sell
 * falls back `USDC -> main USD stable -> native` for chains without USDC (e.g.
 * USDT0 on Plasma). Independent of wallet balances - see ADR 0001.
 */
export function getDefaultPairForChain(chainId: number): {
  buyAddress: ChainAddress;
  sellAddress: ChainAddress;
} {
  const nativeAddress = NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId];
  const sellAddress =
    USDC_ADDRESSES[chainId] ?? MAIN_USD_STABLE_ADDRESSES[chainId] ?? nativeAddress;
  const buyAddress = chainId === CHAIN_IDS.ARC ? COOL_ADDRESS_ARC : nativeAddress;

  return { buyAddress, sellAddress };
}

/**
 * Resolves the default trade pair (sell USDC -> buy native) on the connected
 * wallet's chain, falling back to mainnet when the wallet is missing or on an
 * unsupported chain.
 */
export function getDefaultTradeParams(
  evmWallet?: MatchaConnectedWallet<ConnectedWallet>,
  svmWallet?: MatchaConnectedWallet<ConnectedStandardSolanaWallet>,
) {
  const evmChainId =
    evmWallet != null ? getChainIdNumberFromCaip2ChainId(evmWallet.chainId) : undefined;
  const svmChainId = svmWallet ? CHAIN_IDS.SOLANA : undefined;
  const walletChainId = evmChainId ?? svmChainId;

  // Fall back to mainnet if the wallet chain is missing or unsupported
  const isSupported = walletChainId != null && SUPPORTED_CHAIN_IDS.includes(walletChainId);
  const chainId = isSupported ? walletChainId : CHAIN_IDS.MAINNET;

  const { buyAddress, sellAddress } = getDefaultPairForChain(chainId);

  return {
    buyAddress,
    buyChain: chainId.toString(),
    sellAddress,
    sellChain: chainId.toString(),
  };
}

/** Whether the current EVM swap needs a manual on-chain allowance before submission. */
export function getIsApprovalRequired({
  isWrap,
  isSolana,
  isSmartWallet,
  isCurrentSwapGasless,
  allowance,
  currentPrice,
}: {
  isWrap: boolean;
  isSolana: boolean;
  isSmartWallet: boolean;
  isCurrentSwapGasless: boolean;
  allowance: bigint | undefined;
  currentPrice: Price | Quote | undefined;
}) {
  if (isWrap || isSolana || currentPrice === undefined) {
    return false;
  }

  // Smart wallets handle approvals atomically for regular swaps, but for gasless
  // trades the relayer executes the swap so the token must be pre-approved on-chain.
  if (isSmartWallet && !isCurrentSwapGasless) {
    return false;
  }

  // A signature-based approval replaces the manual on-chain approval.
  // Exclude smart wallets from this check.
  if ('approval' in currentPrice && currentPrice.approval !== null && !isSmartWallet) {
    return false;
  }

  if (!currentPrice.issues.allowance?.spender) {
    return false;
  }

  return allowance !== undefined && allowance < BigInt(currentPrice.sellAmount);
}
